import React, { useEffect, useMemo, useState } from "react";

import { RoutePath } from "routes/route-path";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@headlessui/react";
import moment from "moment";
import { Anchor, CalendarDays, FileBarChart2, Info, Pencil, Plus, Search, Stethoscope } from "lucide-react";

import CountUp from "react-countup";
import Flatpickr from "react-flatpickr";
import Select from "react-select";

import BreadCrumb from "common/breadcrumb";
import TableContainer from "common/table-container";
import LoadingComponent from "common/components/loader";
import SearchNotFoundComponent from "common/components/search-not-found";
//import filterDataBySearch from "common/filter-data-by-search";

import { Tenant } from "models/tenant/tenant";
import { TenantSearchParam } from "models/tenant/tenant-search-param";
import { TenantSearch } from "api/tenant";
import { TenantType } from "models/enums";
import { TenantTypes } from "models/settings/branding";

import { BrandingData } from "admin-portal/settings/branding";

const TenantSearchPage: React.FC = () => {
  const title = "Search";
  const pageTitle = "Manage " + BrandingData.bussinessDomain;
  const navigate = useNavigate();

  const [data, setData] = useState<Tenant[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [searchParam, setSearchParam] = useState<TenantSearchParam>({});
  // Get Data
  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async (param?: TenantSearchParam) => {
    setLoading(true);
    const res = await TenantSearch(param, undefined, 10, 1);
    if (!(res?.status?.isError ?? false) && res?.data) {
      setData(res?.data ?? []);
      setTotal(res?.totalResultCount ?? 0);
    } else {
      setData([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const onSearch = () => {
    loadTenants(searchParam);
  };
  // Search Data
  const filterSearchData = (e: any) => {
    const search = e.target.value;
    //const keysToSearch = ["leaveType", "reason", "noOfDays", "from", "to", "approvedBy", "status"];
    //filterDataBySearch(dataList, search, keysToSearch, setData);
    loadTenants({ name: search ?? "" });
  };

  // Function to handle date selection change
  const handleCreatedFromDateChange = (selectedDates: Date[] | undefined) => {
    if (selectedDates && selectedDates.length > 0) {
      setSearchParam({ ...searchParam, createdDateFrom: moment(selectedDates[0]).format("YYYY-MM-DDTHH:mm:ss") });
    }
  };
  const handleCreatedToDateChange = (selectedDates: Date[] | undefined) => {
    if (selectedDates && selectedDates.length > 0) {
      setSearchParam({ ...searchParam, createdDateTo: moment(selectedDates[0]).format("YYYY-MM-DDTHH:mm:ss") });
    }
  };
  /* const handleSeactivatedFromDateChange = (selectedDates: Date[] | undefined) => {
    if (selectedDates && selectedDates.length > 0) {
      setSearchParam({ ...searchParam, deactivatedDateFrom: selectedDates[0] || null });
    }
  };
  const handleDeactivatedToDateChange = (selectedDates: Date[] | undefined) => {
    if (selectedDates && selectedDates.length > 0) {
      setSearchParam({ ...searchParam, deactivatedDateTo: selectedDates[0] || null });
    }
  }; */
  /*   SYSTEM = 1,
  PROSPECT = 2,
  TRIAL = 3,
  SUBCRIBED = 4
   */
  const TenantTypeComponent = ({ item, entity }: any) => {
    const _tenant = entity as Tenant;
    var _stSort = (_tenant?.subscriptions ?? []).sort((a, b) =>
      a.insertedTime! > b.insertedTime! ? -1 : a.insertedTime! < b.insertedTime! ? 1 : 0
    );
    var _sub = _stSort.length > 0 ? _stSort![0] : null;

    if (!(_tenant.isActive ?? false)) {
      return (
        <span className="px-2.5 py-0.5 inline-block text-xs font-medium rounded border bg-red-100 border-transparent text-red-500 dark:bg-red-500/20 dark:border-transparent">
          INACTIVE
        </span>
      );
    }

    switch (_tenant.tenantTypeId) {
      case TenantType.SUBSCRIBED:
        return (
          <>
            <span className="px-2.5 py-0.5 inline-block text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent">
              {item}
            </span>
            <br />
            {_sub && <span>{_sub.subscriptionPlan?.name}</span>}
          </>
        );
      case TenantType.TRIAL:
        return (
          <>
            <span className="px-2.5 py-0.5 inline-block text-xs font-medium rounded border bg-yellow-100 border-transparent text-yellow-500 dark:bg-yellow-500/20 dark:border-transparent">
              {item}
            </span>
            <br />
            {_sub && <span>{_sub.subscriptionPlan?.name}</span>}
          </>
        );
      case TenantType.PROSPECT:
        return (
          <>
            <span className="px-2.5 py-0.5 inline-block text-xs font-medium rounded border bg-orange-200 border-transparent text-yellow-500 dark:bg-yellow-500/20 dark:border-transparent">
              {item}
            </span>
            <br />
            {_sub && <span>{_sub.subscriptionPlan?.name}</span>}
          </>
        );
      default:
        return (
          <>
            <span className="px-2.5 py-0.5 inline-block text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent">
              {item}
            </span>
            <br />
            {_sub && <span>{_sub.subscriptionPlan?.name}</span>}
          </>
        );
    }
  };

  const handleEditClick = (id: string) => {
    navigate(`${RoutePath.Admin_TenantEdit}?id=${id}`);
  };

  // domain: string //branding name
  //webSiteName: string
  // isActive: boolean
  // tenantTypeId: TenantType

  const columns = useMemo(
    () => [
      {
        header: "Company Name",
        accessorKey: "companyName",
        enableColumnFilter: false,
      },
      {
        header: "Company Email",
        accessorKey: "companyEmail",
        enableColumnFilter: false,
      },
      {
        header: "Contact Number",
        accessorKey: "contactNumber",
        enableColumnFilter: false,
      },
      {
        header: "Web Site Name",
        accessorKey: "webSiteName",
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "tenantTypeId",
        enableColumnFilter: false,
        cell: (cell: any) => <TenantTypeComponent item={cell.getValue()} entity={cell.row.original} />,
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="flex justify-end gap-2">
            <div
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-slate-500 bg-slate-100 hover:text-white hover:bg-slate-500 dark:text-zink-200 dark:bg-zink-600 dark:hover:text-white dark:hover:bg-zink-400 cursor-pointer"
              onClick={() => handleEditClick(cell.row.original.id)}
            >
              <Pencil className="size-4" />
            </div>
            <Link
              to="#!"
              data-modal-target="leaveOverviewModal"
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-custom-500 bg-custom-100 hover:text-white hover:bg-custom-500 dark:bg-custom-500/20 dark:hover:bg-custom-500"
            >
              <Info className="size-4" />
            </Link>
          </div>
        ),
      },
    ],
    []
  );

  // Table Head Action On Right
  useEffect(() => {
    const tableHead = document.querySelectorAll("th");
    const lastTheadElement = tableHead[tableHead.length - 1];
    if (lastTheadElement && lastTheadElement.classList != undefined) {
      lastTheadElement.classList.add("ltr:text-right", "rtl:text-left");
    }

    return () => {
      if (lastTheadElement && lastTheadElement.classList != undefined) {
        lastTheadElement.classList.remove("ltr:text-right", "rtl:text-left");
      }
    };
  });

  return (
    <React.Fragment>
      <BreadCrumb title={title} pageTitle={pageTitle} />
      <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2 xl:grid-cols-12">
        <div className="xl:col-span-3">
          <div className="card">
            <div className="flex items-center gap-3 card-body">
              <div className="flex items-center justify-center size-12 text-red-500 bg-red-100 rounded-md text-15 dark:bg-red-500/20 shrink-0">
                <FileBarChart2 />
              </div>
              <div className="grow">
                <h5 className="mb-1 text-16">
                  <CountUp end={23} className="counter-value" />
                </h5>
                <p className="text-slate-500 dark:text-zink-200">Total Trail Expired</p>
              </div>
            </div>
          </div>
        </div>
        <div className="xl:col-span-3">
          <div className="card">
            <div className="flex items-center gap-3 card-body">
              <div className="flex items-center justify-center size-12 text-green-500 bg-green-100 rounded-md text-15 dark:bg-green-500/20 shrink-0">
                <CalendarDays />
              </div>
              <div className="grow">
                <h5 className="mb-1 text-16">
                  <CountUp end={12} className="counter-value" />
                </h5>
                <p className="text-slate-500 dark:text-zink-200">Renewal Pending</p>
              </div>
            </div>
          </div>
        </div>
        <div className="xl:col-span-3">
          <div className="card">
            <div className="flex items-center gap-3 card-body">
              <div className="flex items-center justify-center size-12 text-purple-500 bg-purple-100 rounded-md text-15 dark:bg-purple-500/20 shrink-0">
                <Stethoscope />
              </div>
              <div className="grow">
                <h5 className="mb-1 text-16">
                  <CountUp end={4} className="counter-value" />
                </h5>
                <p className="text-slate-500 dark:text-zink-200">Pending</p>
              </div>
            </div>
          </div>
        </div>
        <div className="xl:col-span-3">
          <div className="card">
            <div className="flex items-center gap-3 card-body">
              <div className="flex items-center justify-center size-12 rounded-md text-sky-500 bg-sky-100 text-15 dark:bg-sky-500/20 shrink-0">
                <Anchor />
              </div>
              <div className="grow">
                <h5 className="mb-1 text-16">
                  <CountUp end={11} className="counter-value" />
                </h5>
                <p className="text-slate-500 dark:text-zink-200">Pending Approval</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card" id="ordersTable">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-3">
              <div className="relative">
                <input
                  type="text"
                  className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Search by Company Name"
                  autoComplete="off"
                  onChange={(e) => setSearchParam({ ...searchParam, name: e.target.value })}
                />
                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
              </div>
            </div>
            <div className="xl:col-span-3">
              <div className="relative">
                <input
                  type="text"
                  className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Search by Company Email"
                  autoComplete="off"
                  onChange={(e) => setSearchParam({ ...searchParam, email: e.target.value })}
                />
                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
              </div>
            </div>
            <div className="xl:col-span-3">
              <div className="relative">
                <input
                  type="text"
                  className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Search by Phone"
                  autoComplete="off"
                  onChange={(e) => setSearchParam({ ...searchParam, phoneNo: e.target.value })}
                />
                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
              </div>
            </div>
            <div className="xl:col-span-2 xl:col-start-11">
              <div className="ltr:lg:text-right rtl:lg:text-left">
                <Link
                  to={RoutePath.Admin_TenantOnboard}
                  type="button"
                  className="text-white btn  min-w-40 bg-custom-800 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                >
                  <Plus className="inline-block size-4" />{" "}
                  <span className="align-middle">Onboard {BrandingData.bussinessDomain}</span>
                </Link>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-3">
              <label htmlFor="fromInput" className="inline-block mb-2 text-base font-medium">
                {BrandingData.bussinessDomain} Category
              </label>
              <Select
                className="border-slate-200 focus:outline-none focus:border-custom-500"
                options={TenantTypes}
                isSearchable={false} // To disable search
                name="tenantType"
                id="tenantType"
              />
            </div>
            <div className="xl:col-span-3">
              <label htmlFor="fromInput" className="inline-block mb-2 text-base font-medium">
                Created From
              </label>
              <Flatpickr
                id="fromInput"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                options={{
                  dateFormat: "d M, Y",
                }}
                placeholder="Select Date"
                onChange={handleCreatedFromDateChange}
              />
            </div>
            <div className="xl:col-span-3">
              <label htmlFor="toInput" className="inline-block mb-2 text-base font-medium">
                Created To
              </label>
              <Flatpickr
                id="toInput"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                options={{
                  dateFormat: "d M, Y",
                }}
                placeholder="Select Date"
                onChange={handleCreatedToDateChange}
              />
            </div>
            <div className="xl:col-span-2 xl:col-start-11">
              <div className="ltr:lg:text-right rtl:lg:text-left">
                <Button
                  type="button"
                  className="text-white btn min-w-40
                  bg-custom-500 border-custom-500 
                  hover:text-white hover:bg-custom-600 hover:border-custom-600 
                  focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring 
                  focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 
                  active:ring active:ring-custom-100 dark:ring-custom-400/20"
                  onClick={onSearch}
                >
                  <Search className="inline-block size-4" /> <span className="align-middle">Search</span>
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingComponent></LoadingComponent>
          ) : (
            <div className="overflow-x-auto mt-2">
              {data && data.length > 0 ? (
                <TableContainer
                  isPagination={true}
                  columns={columns || []}
                  data={data || []}
                  totalCount={total}
                  customPageSize={10}
                  divclassName="overflow-x-auto"
                  tableclassName="w-full whitespace-nowrap"
                  theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                  thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                  tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                  PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                />
              ) : (
                <SearchNotFoundComponent title="Sorry! No Result Found">
                  <p className="mb-0 text-slate-500 dark:text-zink-200">
                    We've searched {BrandingData.bussinessDomain.toLocaleLowerCase()} matched to your criteria, We did
                    not find any.
                  </p>
                </SearchNotFoundComponent>
              )}
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};
export default TenantSearchPage;
